import { ESCROW_AGENT_DISCLOSURE_PLAIN, type NotificationDTO, type NotificationType } from '@siliconx/shared';
import { formatUsd } from './format';

/**
 * Client-side rendering for the in-app notification feed (SIL-160, layer 5 of
 * the notifications epic SIL-155).
 *
 * The API (SIL-159) returns structured facts only, never pre-rendered copy —
 * `renderNotification` is the ONE place that turns `{ type, payload }` into
 * the title/detail strings shown in the bell dropdown and /notifications page.
 * Keep every type's copy here, not scattered across components.
 */

export interface NotificationCopy {
  title: string;
  detail?: string;
}

// NotificationDTO is a discriminated union on `type` — extract each member's
// payload type from it rather than importing the payload schemas directly, so
// this file stays in sync with whatever the DTO actually carries.
type NotificationOf<T extends NotificationType> = Extract<NotificationDTO, { type: T }>;

function formatAmount(amount: number, currency: string): string {
  return currency === 'USD' ? formatUsd(amount) : `${amount} ${currency}`;
}

/** 'on Mar 3, 2027' — or 'today' when the settlement date is today. */
function formatSettleDate(isoDate: string): string {
  const today = new Date().toISOString().slice(0, 10);
  if (isoDate === today) return 'today';
  const d = new Date(`${isoDate}T00:00:00`);
  return `on ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

export function renderNotification(notification: NotificationDTO): NotificationCopy {
  switch (notification.type) {
    case 'submission_approved': {
      const { assetCount, assetClass } = (notification as NotificationOf<'submission_approved'>).payload;
      return { title: `Submission approved — ${assetCount} × ${assetClass} added to your inventory` };
    }
    case 'submission_rejected': {
      const { assetCount, assetClass, reason } = (notification as NotificationOf<'submission_rejected'>).payload;
      return {
        title: reason
          ? `Submission not approved — ${assetCount} × ${assetClass}: ${reason}`
          : `Submission not approved — ${assetCount} × ${assetClass}`,
      };
    }
    case 'offer_received': {
      const { amount, currency, skuLabel } = (notification as NotificationOf<'offer_received'>).payload;
      return { title: `Offer of ${formatAmount(amount, currency)} received on your ${skuLabel} listing` };
    }
    case 'offer_rescinded': {
      const { skuLabel } = (notification as NotificationOf<'offer_rescinded'>).payload;
      return { title: `Offer withdrawn on your ${skuLabel} listing` };
    }
    case 'offer_refused': {
      const { reason, skuLabel } = (notification as NotificationOf<'offer_refused'>).payload;
      return { title: `Offer refused — ${reason}`, detail: skuLabel };
    }
    case 'purchase_completed': {
      const { skuLabel } = (notification as NotificationOf<'purchase_completed'>).payload;
      return { title: `Purchase completed — ${skuLabel}` };
    }
    case 'forward_sale_committed': {
      const { amount, currency, skuLabel, availableOn, side } =
        (notification as NotificationOf<'forward_sale_committed'>).payload;
      const date = formatSettleDate(availableOn);
      return side === 'seller'
        ? {
            title: `Forward sale committed — ${skuLabel} for ${formatAmount(amount, currency)}`,
            detail: `Binding deal: ownership transfers ${date}. If you cannot deliver, Escrow.com returns the buyer's deposit under the escrow instructions. ${ESCROW_AGENT_DISCLOSURE_PLAIN}`,
          }
        : {
            title: `Forward purchase locked in — ${skuLabel} for ${formatAmount(amount, currency)}`,
            detail: `Your deposit is held by Escrow.com; ownership transfers to you ${date}. ${ESCROW_AGENT_DISCLOSURE_PLAIN}`,
          };
    }
    case 'settlement_reminder': {
      const { skuLabel, availableOn, side } =
        (notification as NotificationOf<'settlement_reminder'>).payload;
      return {
        title: `Settlement approaching — ${skuLabel} settles ${formatSettleDate(availableOn)}`,
        detail: side === 'seller' ? 'Ownership transfers automatically on that date.' : undefined,
      };
    }
    case 'forward_sale_settled': {
      const { amount, currency, skuLabel, side } =
        (notification as NotificationOf<'forward_sale_settled'>).payload;
      return side === 'seller'
        ? { title: `Forward sale settled — ${skuLabel} sold for ${formatAmount(amount, currency)}` }
        : { title: `Forward purchase settled — ${skuLabel} is now yours` };
    }
    case 'listing_expired': {
      const { skuLabel, directOfferId } =
        (notification as NotificationOf<'listing_expired'>).payload;
      return {
        title: directOfferId
          ? `Direct offer expired — ${skuLabel}`
          : `Listing expired — ${skuLabel}`,
      };
    }
    case 'auction_won': {
      const { amount, currency, skuLabel, reserveMet } =
        (notification as NotificationOf<'auction_won'>).payload;
      return reserveMet
        ? { title: `You won the auction — ${skuLabel} for ${formatAmount(amount, currency)}` }
        : {
            title: `Your bid was the highest — ${skuLabel} at ${formatAmount(amount, currency)}`,
            detail: 'Reserve was not met; your bid is now a pending offer the seller can accept or counter.',
          };
    }
    case 'auction_lost': {
      const { skuLabel, winningBidAmount, winningBidRangeLowUsd, winningBidRangeHighUsd, currency } =
        (notification as NotificationOf<'auction_lost'>).payload;
      // SIL-1334: winningBidAmount is withheld from an unverified buyer —
      // fall back to the bucketed range, or no detail at all if neither
      // is present (should not happen, but never throw on a missing field).
      const detail =
        winningBidAmount != null
          ? `The winning bid was ${formatAmount(winningBidAmount, currency)}.`
          : winningBidRangeLowUsd != null && winningBidRangeHighUsd != null
            ? `The winning bid was between ${formatAmount(winningBidRangeLowUsd, currency)} and ${formatAmount(winningBidRangeHighUsd, currency)}.`
            : undefined;
      return { title: `Auction closed — ${skuLabel}`, detail };
    }
    case 'requirement_overdue': {
      const { stepKey, skuLabel } =
        (notification as NotificationOf<'requirement_overdue'>).payload;
      return {
        title: `Action needed — ${skuLabel} is overdue on a required step`,
        detail: `${stepKey.replace(/_/g, ' ')} was due and is still outstanding.`,
      };
    }
    case 'listing_interest_received': {
      const { skuLabel } = (notification as NotificationOf<'listing_interest_received'>).payload;
      return { title: `New interest in your ${skuLabel} listing` };
    }
    case 'buy_now_requested': {
      const { amount, currency, skuLabel } = (notification as NotificationOf<'buy_now_requested'>).payload;
      return { title: `Buy now request — ${formatAmount(amount, currency)} for your ${skuLabel} listing` };
    }
    case 'buy_now_confirmed': {
      const { amount, currency, skuLabel } = (notification as NotificationOf<'buy_now_confirmed'>).payload;
      return { title: `Buy now confirmed — ${skuLabel} for ${formatAmount(amount, currency)}` };
    }
    case 'buy_now_declined': {
      const { skuLabel } = (notification as NotificationOf<'buy_now_declined'>).payload;
      return { title: `Buy now declined — ${skuLabel}` };
    }
    case 'offer_expired': {
      const { skuLabel } = (notification as NotificationOf<'offer_expired'>).payload;
      return {
        title: `Offer expired — ${skuLabel}`,
        detail: 'This item is no longer available, so your open offer was closed.',
      };
    }
    case 'escrow_party_email_changed': {
      const { party, maskedEmail, audience } = (notification as NotificationOf<'escrow_party_email_changed'>).payload;
      const who = party === 'buyer' ? 'buyer' : 'seller';
      return audience === 'affected'
        ? {
            title: `Escrow contact email updated - ${who} address now ${maskedEmail}`,
            detail: `SiliconX updated the ${who} email on this deal's Escrow.com transaction from your organization's contact email. If you did not expect this, contact support. ${ESCROW_AGENT_DISCLOSURE_PLAIN}`,
          }
        : {
            title: `Escrow contact email updated - the ${who}'s address is now ${maskedEmail}`,
            detail: `The ${who} email on this deal's Escrow.com transaction was updated from their organization's contact email. If you did not expect this, contact support. ${ESCROW_AGENT_DISCLOSURE_PLAIN}`,
          };
    }
    default: {
      // Exhaustiveness guard — the drift test in packages/shared catches new
      // types added to NOTIFICATION_TYPES before this ever runs.
      const _exhaustive: never = notification;
      return _exhaustive;
    }
  }
}

export function isNotificationLinkable(type: NotificationType): boolean {
  return true;
}

/** Bell badge label: hidden at 0 (return null), capped display past 9. */
export function unreadBadgeLabel(count: number): string | null {
  if (count <= 0) return null;
  return count > 9 ? '9+' : String(count);
}

/** "3d ago", "2h ago", "5m ago" relative to now (mirrors ops skus_.$skuId.tsx). */
export function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * Pure helper mirroring what the "mark read" click handlers do to a locally
 * held notification list — extracted so the read-on-click behavior is
 * testable without mounting the dropdown/page components (matches this
 * repo's existing plain-vitest test style, see AppShell's
 * dual-profile-nav.test.ts).
 */
export function markRead(list: NotificationDTO[], id: string): NotificationDTO[] {
  return list.map((n) => (n.id === id ? { ...n, read: true } : n));
}

export function markAllRead(list: NotificationDTO[]): NotificationDTO[] {
  return list.map((n) => (n.read ? n : { ...n, read: true }));
}
